import { useEffect, useMemo, useState } from 'react';
import { A4_PAGE_HEIGHT, A4_PAGE_WIDTH, DEFAULT_FONT } from '@/constants';
import { useAuth } from '@/context/AuthContext';
import { ReportMeasurementProvider } from '@/context/ReportMeasurementContext';
import { useReportSections } from '@/hooks/use-report-sections';
import { cn } from '@/lib/utils';
import { resolvePreviewUrl } from '@/utils/preview-cache';
import PreviewPsychologistSignature from '../PreviewPsychologistSignature';
import { ReportFooter } from '../ReportFooter';
import { ReportPageFootnotes } from '../ReportFootnotes';
import { ReportHeader } from '../ReportHeader';
import { useReportFootnotes } from '../useReportFootnotes';
import { useReportPagination } from '../useReportPagination';
import StandaloneFixedMeasurer from './StandaloneFixedMeasurer';
import StandaloneHiddenReportMeasurer from './StandaloneHiddenReportMeasurer';
import StandalonePatientInfoTable from './StandalonePatientInfoTable';
import StandaloneReportSection from './StandaloneReportSection';
import type { RenderableSection } from '../pagination/types';
import type { Psychologist, ReportData, Section } from '../types';

interface StandaloneReportPreviewProps {
    data: ReportData;
}

export default function StandaloneReportPreview({ data }: StandaloneReportPreviewProps) {
    const { user } = useAuth();
    const sections = useReportSections(data) as Section[];

    return (
        <ReportMeasurementProvider>
            <StandaloneReportPreviewContent data={data} sections={sections} psychologist={user} />
        </ReportMeasurementProvider>
    );
}

function StandaloneReportPreviewContent({
    data,
    sections,
    psychologist,
}: {
    data: ReportData;
    sections: Section[];
    psychologist: Psychologist | null;
}) {
    const [shouldMeasureDetailedSections, setShouldMeasureDetailedSections] = useState(false);
    const footnotePlan = useReportFootnotes(data);

    useEffect(() => {
        const frameId = window.requestAnimationFrame(() => {
            setShouldMeasureDetailedSections(true);
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, []);

    const { totalPages, getPageSections, getPageFootnotes, isPaginationReady } =
        useReportPagination(sections, data, undefined, footnotePlan);

    const pagesToRender = useMemo(
        () => Array.from({ length: totalPages }, (_, index) => index + 1),
        [totalPages],
    );

    const headerCustomizations = data.customizations?.header;
    const letterheadUrl = resolvePreviewUrl(headerCustomizations?.letterhead_url);
    const showLetterhead = headerCustomizations?.show_letterhead && !!letterheadUrl;

    return (
        <div
            className="preview-ui-font relative mx-auto flex flex-col gap-8"
            data-report-pagination-ready={isPaginationReady ? 'true' : 'false'}
            style={{
                fontFamily: data.customizations?.font?.family || DEFAULT_FONT,
                width: A4_PAGE_WIDTH,
            }}
        >
            <StandaloneFixedMeasurer data={data} psychologist={psychologist} />

            {shouldMeasureDetailedSections && (
                <StandaloneHiddenReportMeasurer
                    data={data}
                    psychologist={psychologist}
                    footnotePlan={footnotePlan}
                />
            )}

            {pagesToRender.map((page) => {
                const pageSections = getPageSections(page) as RenderableSection[];
                const pageFootnotes = getPageFootnotes(page);
                const isLastPage = page === totalPages;

                return (
                    <div
                        key={page}
                        id={`report-page-${page}`}
                        className="report-paper flex flex-col overflow-visible bg-white shadow-xl"
                        style={{
                            height: A4_PAGE_HEIGHT,
                            ...(showLetterhead
                                ? {
                                      backgroundImage: `url(${letterheadUrl})`,
                                      backgroundSize: '100% 100%',
                                      backgroundPosition: 'center',
                                      backgroundRepeat: 'no-repeat',
                                  }
                                : {}),
                        }}
                    >
                        <div className={cn('flex-1 overflow-hidden p-12')}>
                            <ReportHeader data={data} psychologist={psychologist} />

                            {page === 1 && (
                                <StandalonePatientInfoTable
                                    data={data}
                                    psychologist={psychologist}
                                />
                            )}

                            {pageSections.map((section) => (
                                <StandaloneReportSection
                                    key={section.key}
                                    section={section}
                                    data={data}
                                    blocksOverride={section.blocksOverride}
                                    footnotePlan={footnotePlan}
                                />
                            ))}

                            {isLastPage && sections.length > 0 && (
                                <PreviewPsychologistSignature
                                    full_name={psychologist?.full_name}
                                    crp={psychologist?.crp}
                                    city={psychologist?.city}
                                    state={psychologist?.state}
                                />
                            )}
                        </div>

                        <ReportPageFootnotes footnotes={pageFootnotes} />

                        <ReportFooter
                            currentPage={page}
                            totalPages={totalPages}
                            data={data}
                            psychologist={psychologist}
                        />
                    </div>
                );
            })}
        </div>
    );
}
